import Service from '@ember/service';
import Model from '@ember-data/model';
import { inject as service } from '@ember/service';
import { A } from '@ember/array';

export default class ShData extends Service {

  @service store;

  /**
   * @param {DS.Model|string} model
   * @return {string}
   */
  getModelId(model) {
    return (model instanceof Model) ? model.id : model;
  }  

  /**
   * Add an `include` query param to the options for this request.
   *
   * @param {Object} options
   * @param {Array<String>} includes
   * @return {Object}
   */
  addIncludesToOptions(options, includes) {
    includes = includes.join(',');
    if (includes) {
      if (!options.params) {
        options.params = {};
      }

      options.params['include'] = includes;
    }

    return options;
  }

  /**
   * Given a JSON object response and a model name, parse out all of the actual models in an array and return it.
   *
   * NOTE --- THIS FUNCTION RELIES ON THE RESPONSE ALREADY BEING PUSHED INTO THE STORE.
   *
   * @param {Object} response
   * @param {String} modelName
   * @return {Ember.A<modelName>}
   */
  getModelArrayFromResponse(response, modelName) {
    let models = A();

    if (!response || !response.data || !response.data.forEach) {
      return models;
    }

    response.data.forEach((modelJson) => {
      models.pushObject(this.store.peekRecord(modelName, modelJson.id));
    });

    return models;
  }

  /**
   * Get a pagination model from the raw JSON response if there is one.
   *
   * @param {Object} response
   * @return {null|Pagination}
   */
  getPaginationModelFromResponse(response) {
    if (!response.meta || !response.meta.pagination) {
      return null;
    }

    let paginationModel = this.store.createRecord('pagination'),
      paginationJson = response.meta.pagination;

    paginationModel.setProperties({
      total: paginationJson.total,
      totalPages: paginationJson.total_pages,
      perPage: paginationJson.per_page,
      currentPage: paginationJson.current_page,
      count: paginationJson.count,
    });

    return paginationModel;
  }

  /**
   * Given a response, push all data and includes into the store
   *
   * @param {Object} response     well fromed JSON-api response as defined by JSON-API spec.
   * @param {Array=} opt_preserveRelationships    This is an array of relationships indices that should be preserved
   *                                              on the top level objects that are being pushed to the store.
   *
   *                                              e.g. If you know there are relationships in the store that you know
   *                                              you don't want overridden, you can set that relationship here.
   */
  pushResponseToStore(response, opt_preserveRelationships = []) {
    /**
     * Our internal closure to push the payload to the store
     *
     * @param {Object} model    Json model object of the data we want to push into the store.
     */
    let pushPayloadToStore = (model) => {
      let existingModel = this.store.peekRecord(model.type, model.id);
      const preservedRelationships = {};

      // If we opted to preserve all relationships, fetch them
      if (existingModel) {
        existingModel.eachRelationship((name, descriptor) => {
          if (opt_preserveRelationships[name]) {
            preservedRelationships[name] = {
              descriptor: descriptor,
              value: existingModel.get(name),
            };
          }
        });
      }

      this.store.pushPayload({
        data: model,
      });

      // data been pushed into the store, and there is no existing model to check, so just return.
      if (!existingModel) {
        return;
      }

      let currentModel = this.store.peekRecord(model.type, model.id);

      // update the realtionship with the existing model's relationship data.
      Object.keys(preservedRelationships).forEach(key => {
        if (preservedRelationships[key].descriptor.kind === 'hasMany') {
          const currentModelRelationship = currentModel.get(key);
          preservedRelationships[key].value.forEach((item) => {
            currentModelRelationship.pushObject(item);
          });
        } else {
          currentModel.set(key, preservedRelationships[key].value);
        }
      });
    };

    // Included models have to be stored first to avoid re-fetching
    if (response.included) {
      response.included.forEach((model) => {
        this.store.pushPayload({
          data: model,
        });
      });
    }

    if (response.data) {
      if (response.data.forEach) {
        response.data.forEach((model) => {
          pushPayloadToStore(model);
        });
      } else {
        pushPayloadToStore(response.data);
      }
    }
  }
}
